import { DndContext } from '@dnd-kit/core';
import AssistantIcon from '@mui/icons-material/Assistant';
import History from '@mui/icons-material/History';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import useRefreshAfterTreadAgentWrites from '~hooks/useTreadAgent/useRefreshAfterTreadAgentWrites';
import useTreadAgentConversationsQuery from '~queries/useTreadAgentConversationsQuery';
import { useStores } from '~store';
import TreadAgentLoader from '~tread-agent/TreadAgentLoader';
import TreadAgentPanel, {
  TREAD_AGENT_PANEL_DEFAULT_SIZE_IN_PX,
  TREAD_AGENT_PANEL_MIN_SIZE_IN_PX,
  TreadAgentViewMode,
} from '~tread-agent/TreadAgentPanel';
import TreadAgentTabs, { ExpandedTabStripRenderer } from '~tread-agent/TreadAgentTabs';
import {
  filterConversationsByTitle,
  formatRelativeShort,
  sortConversationsByMostRecentActivity,
} from '~tread-agent/treadAgentUtils';
import useTreadAgentTabs from '~tread-agent/useTreadAgentTabs';
import useDraggableResizablePanel from '~utils/hooks/useDraggableResizablePanel';
import { useAwaitableFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { usePrevious } from '~utils/hooks/usePrevious';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

// Height of the full-width docked "Ask Tread" bar. The rest of the app reserves this much
// space at the bottom of the viewport via the --tread-agent-dock-height CSS variable (see
// src/assets/styles/common/_layout.scss), gated on the body.tread-agent-dock-active class
// this component toggles, so no drawer, dialog, or page ever runs underneath the bar. Keep
// this value in sync with the SCSS variable.
const TREAD_AGENT_DOCK_HEIGHT_IN_PX = 42;
// Class toggled on <body> while the dock is mounted; the stylesheet keys the reserved-space
// variable off it.
const DOCK_ACTIVE_BODY_CLASS = 'tread-agent-dock-active';
const HISTORY_POPOVER_WIDTH_IN_PX = 300;
const HISTORY_LIST_MAX_HEIGHT_IN_PX = 320;
// Float the history card just above the bar: bar height + an 8px gap.
const HISTORY_BOTTOM_OFFSET_IN_PX = TREAD_AGENT_DOCK_HEIGHT_IN_PX + 8;
const PANEL_POSITION_STORAGE_KEY = 'tread.tread_agent.panel_position';
const PANEL_SIZE_STORAGE_KEY = 'tread.tread_agent.panel_size';

type PanelState = 'closed' | 'minimized' | 'open';

const TreadAgentDockContent = observer(() => {
  const theme = useTheme();
  const { treadAgentStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [panelState, setPanelState] = useState<PanelState>('closed');
  const isPanelMounted = panelState !== 'closed';
  const isPanelMinimized = panelState === 'minimized';
  const [viewMode, setViewMode] = useState<TreadAgentViewMode>('compact');
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const historyTriggerRef = useRef<HTMLButtonElement>(null);
  // Only fetch the conversation list once the history popover is opened, so we don't pull
  // history on app load for users who never open it.
  const { conversations, isFetching } = useTreadAgentConversationsQuery(companyId, {
    enabled: isHistoryOpen,
  });

  const [historySearch, setHistorySearch] = useState('');

  useRefreshAfterTreadAgentWrites();

  const {
    modifiers: panelDragModifiers,
    onDragEnd: handlePanelDragEnd,
    position: panelPosition,
    sensors: panelDragSensors,
    size: panelSize,
    startResize: startPanelResize,
  } = useDraggableResizablePanel({
    bottomInsetInPx: TREAD_AGENT_DOCK_HEIGHT_IN_PX,
    defaultSizeInPx: TREAD_AGENT_PANEL_DEFAULT_SIZE_IN_PX,
    minSizeInPx: TREAD_AGENT_PANEL_MIN_SIZE_IN_PX,
    positionStorageKey: PANEL_POSITION_STORAGE_KEY,
    sizeStorageKey: PANEL_SIZE_STORAGE_KEY,
  });

  const filteredConversations = useMemo(
    () =>
      filterConversationsByTitle(
        sortConversationsByMostRecentActivity(conversations),
        historySearch,
      ),
    [conversations, historySearch],
  );

  const handleCloseHistory = useCallback(() => {
    setIsHistoryOpen(false);
    setHistorySearch('');
  }, []);

  // Close on outside click, but ignore the trigger button so its own toggle isn't undone.
  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (historyTriggerRef.current?.contains(event.target as Node)) {
        return;
      }

      handleCloseHistory();
    },
    [handleCloseHistory],
  );

  const closePanel = useCallback(
    (shouldKeepRunningChat: boolean) => {
      setPanelState('closed');
      // Always reopen compact: the expanded overlay is an opt-in, per-session affordance.
      setViewMode('compact');
      treadAgentStore.clearPendingPrompt();

      if (!(shouldKeepRunningChat && treadAgentStore.isTurnInProgress)) {
        treadAgentStore.closeConversation();
      }
    },
    [treadAgentStore],
  );

  const handleClosePanel = useCallback(() => closePanel(true), [closePanel]);
  const handleCloseLastTab = useCallback(() => closePanel(false), [closePanel]);

  const handleToggleViewMode = useCallback(() => {
    setViewMode((mode) => (mode === 'compact' ? 'expanded' : 'compact'));
  }, []);

  const handleMinimizePanel = useCallback(() => setPanelState('minimized'), []);

  const handleOpenPanel = useCallback(() => setPanelState('open'), []);

  const { closeTab, isTabActive, openConversationTab, openDraftTab, selectTab, tabs } =
    useTreadAgentTabs({
      companyId,
      isPanelMinimized,
      isPanelOpen: isPanelMounted,
      onLastTabClosed: handleCloseLastTab,
      onOpenPanel: handleOpenPanel,
    });

  const handleOpenConversation = useCallback(
    (conversationId: string) => {
      handleCloseHistory();
      openConversationTab(conversationId);
    },
    [handleCloseHistory, openConversationTab],
  );

  const { isTurnInProgress } = treadAgentStore;
  const wasTurnInProgress = usePrevious(isTurnInProgress);
  useEffect(() => {
    if (!isPanelMounted && wasTurnInProgress && !isTurnInProgress) {
      treadAgentStore.closeConversation();
    }
  }, [isPanelMounted, isTurnInProgress, wasTurnInProgress]);

  const tabStripProps = {
    companyId,
    isTabActive,
    onCloseTab: closeTab,
    onSelectTab: selectTab,
    tabs,
    workingConversationId: isTurnInProgress ? treadAgentStore.activeConversationId : null,
  };

  const renderExpandedTabStrip: ExpandedTabStripRenderer = ({
    isSkillsViewOpen,
    onLeaveSkillsView,
  }) => (
    <TreadAgentTabs
      {...tabStripProps}
      isTabActive={(tab) => !isSkillsViewOpen && isTabActive(tab)}
      onSelectTab={(tab) => {
        onLeaveSkillsView();
        selectTab(tab);
      }}
      variant="expanded"
    />
  );

  const { pendingPrompt } = treadAgentStore;
  useEffect(() => {
    if (pendingPrompt) {
      setPanelState('open');
    }
  }, [pendingPrompt]);

  const previousCompanyIdRef = useRef(companyId);
  useEffect(() => {
    if (companyId === previousCompanyIdRef.current) {
      return;
    }

    previousCompanyIdRef.current = companyId;
    handleCloseHistory();
  }, [companyId, handleCloseHistory]);

  // Reserve space at the bottom of every page for the docked bar. The global stylesheet keys
  // --tread-agent-dock-height off this body class, so drawers, dialogs, and full-height pages
  // shorten to end above the bar instead of being overlaid by it. Only mounted (and so only
  // toggled) when the feature flag is enabled.
  useEffect(() => {
    document.body.classList.add(DOCK_ACTIVE_BODY_CLASS);

    return () => document.body.classList.remove(DOCK_ACTIVE_BODY_CLASS);
  }, []);

  // The expanded overlay fills the content area, so the floating launcher would only peek out
  // dimmed behind its scrim — hide it while expanded.
  const isLauncherVisible = !(panelState === 'open' && viewMode === 'expanded');

  return (
    <>
      {isPanelMounted && (
        <DndContext
          modifiers={panelDragModifiers}
          onDragEnd={handlePanelDragEnd}
          sensors={panelDragSensors}
        >
          <TreadAgentPanel
            expandedTabStrip={renderExpandedTabStrip}
            isMinimized={isPanelMinimized}
            onClose={handleClosePanel}
            onMinimize={handleMinimizePanel}
            onNewChat={openDraftTab}
            onResizeStart={startPanelResize}
            onSelectConversation={openConversationTab}
            onToggleViewMode={handleToggleViewMode}
            position={panelPosition}
            sizeInPx={panelSize}
            viewMode={viewMode}
          />
        </DndContext>
      )}

      {isLauncherVisible && (
        <Box
          sx={{
            alignItems: 'center',
            // Match the top nav bar's surface color (AppHeader uses background.paper).
            backgroundColor: 'background.paper',
            borderTop: `1px solid ${theme.brandV2.colors.treadGray7}`,
            bottom: 0,
            // Inset shadow on the top edge only: positive y drops it in from the top, and the
            // negative spread keeps it off the left/right/bottom edges.
            boxShadow: 'inset 0 6px 6px -6px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            gap: 1,
            height: `${TREAD_AGENT_DOCK_HEIGHT_IN_PX}px`,
            justifyContent: 'flex-end',
            left: 0,
            position: 'fixed',
            px: 2,
            right: 0,
            // Above page content and drawers, below the floating panel (modal - 1). The rest of
            // the app reserves TREAD_AGENT_DOCK_HEIGHT_IN_PX so nothing renders under this bar.
            zIndex: theme.zIndex.modal - 2,
          }}
        >
          <TreadAgentTabs {...tabStripProps} variant="docked" />

          <SmallButton
            color="brandV2Black"
            onClick={isPanelMinimized ? handleOpenPanel : openDraftTab}
            startIcon={<AssistantIcon sx={{ fontSize: 16 }} />}
            sx={{
              // A rounded pill so the launcher reads as a distinct assistant entry point
              // rather than another rectangular toolbar button.
              '&.MuiButtonBase-root': {
                borderRadius: '999px',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)',
                flexShrink: 0,
                height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
                px: 2,
              },
            }}
          >
            {t('tread_agent.button_label')}
          </SmallButton>

          <IconButton
            aria-label={t('tread_agent.chat_history')}
            onClick={() => setIsHistoryOpen((open) => !open)}
            ref={historyTriggerRef}
            sx={{
              backgroundColor: theme.semantic.surfaceRaised,
              border: `1px solid ${theme.brandV2.colors.treadGray7}`,
              borderRadius: theme.brandV2.borderRadius,
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)',
              flexShrink: 0,
              height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
              width: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
              '&:hover': { backgroundColor: theme.brandV2.colors.treadGray8 },
            }}
          >
            <History sx={{ color: theme.brandV2.colors.treadGray2, fontSize: 18 }} />
          </IconButton>
        </Box>
      )}

      {isHistoryOpen && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            elevation={3}
            sx={{
              border: `1px solid ${theme.brandV2.colors.treadGray7}`,
              borderRadius: theme.brandV2.borderRadius,
              bottom: `${HISTORY_BOTTOM_OFFSET_IN_PX}px`,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              position: 'fixed',
              right: '16px',
              width: `${HISTORY_POPOVER_WIDTH_IN_PX}px`,
              // Above page content and the launcher; matches the panel layer.
              zIndex: theme.zIndex.modal - 1,
            }}
          >
            <InputBase
              fullWidth
              onChange={(event) => setHistorySearch(event.target.value)}
              placeholder={t('tread_agent.search_placeholder')}
              sx={{ fontSize: '13px', px: 1.5, py: 0.75 }}
              value={historySearch}
            />

            <Divider />

            <Box
              sx={{
                maxHeight: `${HISTORY_LIST_MAX_HEIGHT_IN_PX}px`,
                overflowY: 'auto',
                py: 0.5,
              }}
            >
              {isFetching && conversations.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                  <TreadAgentLoader size={20} />
                </Box>
              ) : (
                filteredConversations.length === 0 && (
                  <Typography
                    sx={{
                      color: theme.brandV2.colors.treadGray3,
                      fontSize: '12px',
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    {historySearch.trim()
                      ? t('tread_agent.no_matching_chats')
                      : t('tread_agent.no_chats')}
                  </Typography>
                )
              )}

              {filteredConversations.map((conversation) => (
                <Box
                  key={conversation.id}
                  onClick={() => handleOpenConversation(conversation.id)}
                  sx={{
                    alignItems: 'center',
                    backgroundColor:
                      conversation.id === treadAgentStore.activeConversationId
                        ? theme.brandV2.colors.treadGray8
                        : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'space-between',
                    px: 1.5,
                    py: 0.75,
                    '&:hover': {
                      backgroundColor: theme.brandV2.colors.treadGray8,
                    },
                  }}
                >
                  <Typography noWrap sx={{ fontSize: '13px' }}>
                    {conversation.title || t('tread_agent.untitled_chat')}
                  </Typography>

                  <Typography
                    sx={{
                      color: theme.brandV2.colors.treadGray3,
                      flexShrink: 0,
                      fontSize: '12px',
                    }}
                  >
                    {formatRelativeShort(
                      conversation.lastActivityAt ?? conversation.createdAt,
                    )}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </ClickAwayListener>
      )}
    </>
  );
});

const TreadAgentDock = () => {
  const treadAgentFeatureFlag = useAwaitableFeatureFlag({
    featureFlagKey: FeatureFlags.treadAgent,
  });

  if (!treadAgentFeatureFlag.isReady || !treadAgentFeatureFlag.isFeatureFlagEnabled) {
    return null;
  }

  return <TreadAgentDockContent />;
};

export default TreadAgentDock;
